import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Switch } from '../../components/ui/switch';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../../components/ui/alert-dialog';
import { Badge } from '../../components/ui/badge';
import { 
  CreditCard, Plus, Edit, Trash2, Loader2, 
  CheckCircle2, Star, Eye, EyeOff, GripVertical,
  Users, ArrowUp, ArrowDown
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const PlanosPage = () => {
  const { token } = useAuth();
  const [planos, setPlanos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedPlano, setSelectedPlano] = useState(null);
  const [formData, setFormData] = useState({
    nome: '',
    valor_mensal: 0,
    limite_empregados: 50,
    descricao: '',
    features: [''],
    destaque: false,
    ativo: true,
    ordem: 0
  });

  useEffect(() => {
    fetchPlanos();
  }, []);

  const fetchPlanos = async () => {
    try {
      const response = await fetch(`${API_URL}/api/planos`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        // Ordenar por campo 'ordem'
        setPlanos(data.sort((a, b) => (a.ordem || 0) - (b.ordem || 0)));
      }
    } catch (error) {
      console.error('Error fetching planos:', error);
      toast.error('Erro ao carregar planos');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = (plano = null) => {
    if (plano) {
      setSelectedPlano(plano);
      setFormData({
        nome: plano.nome || '',
        valor_mensal: plano.valor_mensal || 0,
        limite_empregados: plano.limite_empregados || 50,
        descricao: plano.descricao || '',
        features: plano.features?.length > 0 ? plano.features : [''],
        destaque: plano.destaque || false,
        ativo: plano.ativo !== false,
        ordem: plano.ordem || 0
      });
    } else {
      setSelectedPlano(null);
      setFormData({
        nome: '',
        valor_mensal: 0,
        limite_empregados: 50,
        descricao: '',
        features: [''],
        destaque: false,
        ativo: true,
        ordem: planos.length
      });
    }
    setDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    
    // Filtrar features vazias
    const cleanedData = {
      ...formData,
      features: formData.features.filter(f => f.trim() !== '')
    };

    try {
      const url = selectedPlano 
        ? `${API_URL}/api/planos/${selectedPlano.id}`
        : `${API_URL}/api/planos`;
      
      const method = selectedPlano ? 'PUT' : 'POST';
      
      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify(cleanedData)
      });

      if (response.ok) {
        toast.success(selectedPlano ? 'Plano atualizado!' : 'Plano criado!');
        setDialogOpen(false);
        fetchPlanos();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Erro ao salvar plano');
      }
    } catch (error) {
      toast.error('Erro ao salvar plano');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!selectedPlano) return;
    
    setSaving(true);
    try {
      const response = await fetch(`${API_URL}/api/planos/${selectedPlano.id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.ok) {
        toast.success('Plano excluído!');
        setDeleteDialogOpen(false);
        setSelectedPlano(null);
        fetchPlanos();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Erro ao excluir plano');
      }
    } catch (error) {
      toast.error('Erro ao excluir plano');
    } finally {
      setSaving(false);
    }
  };

  const handleToggleAtivo = async (plano) => {
    try {
      const response = await fetch(`${API_URL}/api/planos/${plano.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({ ...plano, ativo: !plano.ativo })
      });

      if (response.ok) {
        toast.success(plano.ativo ? 'Plano desativado' : 'Plano ativado');
        fetchPlanos();
      }
    } catch (error) {
      toast.error('Erro ao alterar status');
    }
  };

  const handleMoveOrder = async (plano, direction) => {
    const currentIndex = planos.findIndex(p => p.id === plano.id);
    const newIndex = direction === 'up' ? currentIndex - 1 : currentIndex + 1;
    
    if (newIndex < 0 || newIndex >= planos.length) return;

    const otherPlano = planos[newIndex];
    
    try {
      // Trocar ordem dos dois planos
      await Promise.all([
        fetch(`${API_URL}/api/planos/${plano.id}`, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`
          },
          body: JSON.stringify({ ...plano, ordem: newIndex })
        }),
        fetch(`${API_URL}/api/planos/${otherPlano.id}`, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`
          },
          body: JSON.stringify({ ...otherPlano, ordem: currentIndex })
        })
      ]);
      
      fetchPlanos();
    } catch (error) {
      toast.error('Erro ao reordenar');
    }
  };

  const addFeature = () => {
    setFormData({
      ...formData,
      features: [...formData.features, '']
    });
  };

  const removeFeature = (index) => {
    const newFeatures = formData.features.filter((_, i) => i !== index);
    setFormData({
      ...formData,
      features: newFeatures.length > 0 ? newFeatures : ['']
    });
  };

  const updateFeature = (index, value) => {
    const newFeatures = [...formData.features];
    newFeatures[index] = value;
    setFormData({ ...formData, features: newFeatures });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-accent" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="planos-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h2 className="text-2xl font-bold text-primary font-['Outfit'] mb-1">Planos</h2>
          <p className="text-muted-foreground">
            Gerencie os planos da plataforma. As alterações são refletidas na landing page.
          </p>
        </div>
        <Button 
          className="bg-accent hover:bg-accent/90" 
          onClick={() => handleOpenDialog()}
          data-testid="novo-plano-btn"
        >
          <Plus className="w-4 h-4 mr-2" />
          Novo Plano
        </Button>
      </div>

      {/* Info Banner */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 flex items-start gap-3">
        <Eye className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
        <div>
          <p className="text-sm text-blue-800 font-medium">Landing Page Sincronizada</p>
          <p className="text-sm text-blue-600">
            Os planos ativos serão exibidos automaticamente na landing page, ordenados conforme definido aqui.
          </p>
        </div>
      </div>

      {/* Planos Grid */}
      {planos.length === 0 ? (
        <Card className="p-12 text-center">
          <CreditCard className="w-12 h-12 text-slate-300 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-slate-700 mb-2">Nenhum plano cadastrado</h3>
          <p className="text-slate-500 mb-4">Crie seu primeiro plano para exibir na landing page.</p>
          <Button onClick={() => handleOpenDialog()} className="bg-accent hover:bg-accent/90">
            <Plus className="w-4 h-4 mr-2" />
            Criar Plano
          </Button>
        </Card>
      ) : (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {planos.map((plano, index) => (
            <Card 
              key={plano.id} 
              className={`relative border-2 transition-all ${
                plano.destaque 
                  ? 'border-accent shadow-lg' 
                  : plano.ativo 
                    ? 'border-slate-200 hover:shadow-md' 
                    : 'border-slate-100 opacity-60'
              }`}
              data-testid={`plano-card-${plano.id}`}
            >
              {/* Badges */}
              <div className="absolute top-3 right-3 flex items-center gap-2">
                {plano.destaque && (
                  <Badge className="bg-accent">
                    <Star className="w-3 h-3 mr-1" />
                    Destaque
                  </Badge>
                )}
                <Badge variant={plano.ativo ? 'default' : 'secondary'} className={plano.ativo ? 'bg-emerald-500' : ''}>
                  {plano.ativo ? 'Ativo' : 'Inativo'}
                </Badge>
              </div>

              {/* Order Controls */}
              <div className="absolute top-3 left-3 flex flex-col gap-1">
                <button
                  onClick={() => handleMoveOrder(plano, 'up')}
                  disabled={index === 0}
                  className="p-1 rounded hover:bg-slate-100 disabled:opacity-30 disabled:cursor-not-allowed"
                >
                  <ArrowUp className="w-4 h-4 text-slate-500" />
                </button>
                <button
                  onClick={() => handleMoveOrder(plano, 'down')}
                  disabled={index === planos.length - 1}
                  className="p-1 rounded hover:bg-slate-100 disabled:opacity-30 disabled:cursor-not-allowed"
                >
                  <ArrowDown className="w-4 h-4 text-slate-500" />
                </button>
              </div>

              <CardHeader className="pt-12">
                <CardTitle className="text-xl font-['Outfit']">{plano.nome}</CardTitle>
                <p className="text-sm text-muted-foreground">{plano.descricao}</p>
              </CardHeader>

              <CardContent className="space-y-4">
                {/* Preço */}
                <div>
                  <p className="text-3xl font-bold text-primary">
                    R$ {plano.valor_mensal?.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                    <span className="text-sm font-normal text-muted-foreground">/mês</span>
                  </p>
                </div>

                {/* Limite */}
                <div className="flex items-center gap-2 text-sm text-slate-600">
                  <Users className="w-4 h-4" />
                  <span>Até {plano.limite_empregados} empregados</span>
                </div>

                {/* Features */}
                {plano.features?.length > 0 && (
                  <ul className="space-y-2 pt-2 border-t">
                    {plano.features.slice(0, 4).map((feature, i) => (
                      <li key={i} className="flex items-start gap-2 text-sm">
                        <CheckCircle2 className="w-4 h-4 text-accent flex-shrink-0 mt-0.5" />
                        <span className="text-slate-600">{feature}</span>
                      </li>
                    ))}
                    {plano.features.length > 4 && (
                      <li className="text-sm text-slate-400">
                        +{plano.features.length - 4} mais...
                      </li>
                    )}
                  </ul>
                )}

                {/* Actions */}
                <div className="flex gap-2 pt-4 border-t">
                  <Button
                    variant="outline"
                    size="sm"
                    className="flex-1"
                    onClick={() => handleOpenDialog(plano)}
                  >
                    <Edit className="w-4 h-4 mr-1" />
                    Editar
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleToggleAtivo(plano)}
                  >
                    {plano.ativo ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    className="text-red-600 hover:text-red-700 hover:bg-red-50"
                    onClick={() => {
                      setSelectedPlano(plano);
                      setDeleteDialogOpen(true);
                    }}
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Dialog Criar/Editar */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-['Outfit']">
              {selectedPlano ? 'Editar Plano' : 'Criar Plano'}
            </DialogTitle>
            <DialogDescription>
              {selectedPlano 
                ? 'Atualize as informações do plano. A landing page será atualizada automaticamente.'
                : 'Configure um novo plano de assinatura para exibir na landing page.'
              }
            </DialogDescription>
          </DialogHeader>

          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Nome */}
            <div className="space-y-2">
              <Label htmlFor="nome">Nome do Plano *</Label>
              <Input
                id="nome"
                value={formData.nome}
                onChange={(e) => setFormData({...formData, nome: e.target.value})}
                placeholder="Ex: Business, Enterprise..."
                required
                data-testid="input-nome"
              />
            </div>

            {/* Valor e Limite */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="valor">Valor Mensal (R$) *</Label>
                <Input
                  id="valor"
                  type="number"
                  step="0.01"
                  min="0"
                  value={formData.valor_mensal}
                  onChange={(e) => setFormData({...formData, valor_mensal: parseFloat(e.target.value) || 0})}
                  required
                  data-testid="input-valor"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="limite">Limite de Empregados *</Label>
                <Input
                  id="limite"
                  type="number"
                  min="1"
                  value={formData.limite_empregados}
                  onChange={(e) => setFormData({...formData, limite_empregados: parseInt(e.target.value) || 1})}
                  required
                  data-testid="input-limite"
                />
              </div>
            </div>

            {/* Descrição */}
            <div className="space-y-2">
              <Label htmlFor="descricao">Descrição</Label>
              <Textarea
                id="descricao"
                value={formData.descricao}
                onChange={(e) => setFormData({...formData, descricao: e.target.value})}
                placeholder="Breve descrição do plano..."
                rows={2}
                data-testid="input-descricao"
              />
            </div>

            {/* Features */}
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label>Recursos Incluídos</Label>
                <Button type="button" variant="ghost" size="sm" onClick={addFeature}>
                  <Plus className="w-4 h-4 mr-1" />
                  Adicionar
                </Button>
              </div>
              <div className="space-y-2">
                {formData.features.map((feature, index) => (
                  <div key={index} className="flex gap-2">
                    <Input
                      value={feature}
                      onChange={(e) => updateFeature(index, e.target.value)}
                      placeholder={`Recurso ${index + 1}`}
                    />
                    {formData.features.length > 1 && (
                      <Button 
                        type="button" 
                        variant="ghost" 
                        size="icon"
                        onClick={() => removeFeature(index)}
                      >
                        <Trash2 className="w-4 h-4 text-red-500" />
                      </Button>
                    )}
                  </div>
                ))}
              </div>
            </div>

            {/* Switches */}
            <div className="space-y-4 pt-4 border-t">
              <div className="flex items-center justify-between">
                <div>
                  <Label htmlFor="destaque">Plano em Destaque</Label>
                  <p className="text-xs text-muted-foreground">
                    Será exibido com visual diferenciado na landing page
                  </p>
                </div>
                <Switch
                  id="destaque"
                  checked={formData.destaque}
                  onCheckedChange={(checked) => setFormData({...formData, destaque: checked})}
                />
              </div>
              <div className="flex items-center justify-between">
                <div>
                  <Label htmlFor="ativo">Plano Ativo</Label>
                  <p className="text-xs text-muted-foreground">
                    Apenas planos ativos aparecem na landing page
                  </p>
                </div>
                <Switch
                  id="ativo"
                  checked={formData.ativo}
                  onCheckedChange={(checked) => setFormData({...formData, ativo: checked})}
                />
              </div>
            </div>

            {/* Buttons */}
            <div className="flex justify-end gap-3 pt-4">
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                Cancelar
              </Button>
              <Button 
                type="submit" 
                className="bg-accent hover:bg-accent/90" 
                disabled={saving}
                data-testid="submit-plano-btn"
              >
                {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                {selectedPlano ? 'Salvar Alterações' : 'Criar Plano'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Dialog Confirmar Exclusão */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Excluir Plano</AlertDialogTitle>
            <AlertDialogDescription>
              Tem certeza que deseja excluir o plano "{selectedPlano?.nome}"? 
              Esta ação não pode ser desfeita e o plano será removido da landing page.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="bg-red-600 hover:bg-red-700"
              disabled={saving}
            >
              {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Excluir
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default PlanosPage;
